import React, { useMemo } from 'react';
import Slider from 'react-slick';
import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image';
import { getGatsbyImage } from 'gatsby-plugin-storyblok-image';
import { array } from 'prop-types';
import styled from 'styled-components';
import { useViewport } from '../../../context/viewport.context';
import { bpWidth, bpWidthTailwind, sizes } from '../../../styles/variables';

const SwipeSlider = styled(Slider)`
  height: 100%;
  width: 100%;

  .gatsby-image-wrapper.gatsby-image-wrapper-constrained.slideImage {
    width: 100%;
  }

  /* Complementary to GridCarousel's "display: none" below tablet (GridCarousel.jsx) — the two
     mount unconditionally side by side and native CSS media queries pick the visible one, so
     there's no JS/viewWidth branching (and no hydration mismatch) deciding which one shows. */
  @media (${bpWidthTailwind.desktopSm}) {
    display: none;
  }
`;

const Slide = styled.div`
  .gatsby-image-wrapper {
    width: 100%;
  }
`;

const SwipeCarousel = (props) => {
  const { variantSlides } = props;
  const { viewWidth } = useViewport();

  const slides = variantSlides;

  const settings = useMemo(
    () => ({
      arrows: false,
      dots: false,
      dotsClass: 'slick-dots slick-thumb',
      fade: false,
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      speed: 500,
      swipe: true,
      touchMove: true,
      swipeToSlide: true,
      variableWidth: true,
      responsive: [
        {
          breakpoint: sizes.containerMinWidth.desktopSm,
          settings: {
            dots: true,
          },
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewWidth],
  );

  return (
    slides &&
    slides.length && (
      <SwipeSlider {...settings}>
        {slides.map((slide, index) => {
          if (!slide.assetLg?.filename) {
            return undefined;
          }

          const loadType = index < 4 ? 'eager' : 'lazy';

          // withArtDirection's artDirected array below already covers every breakpoint via CSS
          // media queries, so this base/fallback image only needs to be a single, viewport-
          // independent default (largest-first) — a viewWidth-based override here would read the
          // client's real width on its first render (before hydration) but the server's
          // build-time default on its render, a genuine hydration mismatch once this component
          // is no longer gated behind an effect (see ProductVariantCarousels.jsx).
          // eslint-disable-next-line max-len
          const imageUrl =
            slide.assetLg?.filename || slide.assetMd?.filename || slide.assetSm?.filename || slide.assetXs?.filename;

          /** @type { GetGatsbyImageOptions } */
          const options = {
            layout: slide.layout,
            quality: slide.quality,
          };

          const artDirected = [];
          if (slide.assetXs?.filename) {
            artDirected.push({
              media: `(${bpWidth.mobile})`,
              image: getGatsbyImage(slide.assetXs.filename, options),
            });
          }

          if (slide.assetSm?.filename) {
            artDirected.push({
              media: `(${bpWidth.tablet})`,
              image: getGatsbyImage(slide.assetSm.filename, options),
            });
          }

          if (slide.assetMd?.filename) {
            artDirected.push({
              media: `(${bpWidth.desktopSm})`,
              image: getGatsbyImage(slide.assetMd.filename, options),
            });
          }

          const imageData = withArtDirection(getGatsbyImage(imageUrl, options), artDirected);
          let media;

          if (slide.mediaType === 'video') {
            const extension = new URL(imageUrl).pathname.split('.').pop();
            const sourceType = extension === 'mp4' ? 'video/mp4' : 'video/webm';

            // eslint-disable-next-line react-hooks/rules-of-hooks
            media = (
              <video autoPlay loop muted playsInline data-video="SwipeCarousel-video">
                <source src={`${imageUrl}`} type={sourceType} />
              </video>
            );
          } else {
            media = <GatsbyImage image={imageData} loading={loadType} alt="" className="slideImage" />;
          }

          return (
            <Slide key={slide._uid}>
              {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
              {media}
            </Slide>
          );
        })}
      </SwipeSlider>
    )
  );
};

SwipeCarousel.propTypes = {
  variantSlides: array,
};
SwipeCarousel.defaultProps = {
  variantSlides: [],
};

export default React.memo(SwipeCarousel);
